import {
  Component,
  computed,
  forwardRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { AmendmentActionButtons } from '@app/features/amendments/amendment-action-buttons/amendment-action-buttons';
import { AmendmentChangesView } from '@app/features/amendments/amendment-changes-view/amendment-changes-view';
import {
  Amendment,
  AmendmentPayload,
  AmendmentReasonCode,
  AmendmentReasons,
  DECLARABLE_REASONS,
} from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { AmendmentSummary } from '@app/features/amendments/amendment-summary/amendment-summary';
import { ContractStatusComponent } from '@app/features/contracts/contract-status/contract-status';
import { SignatureSendFailure } from '@app/features/contracts/signature/signature-send-failure/signature-send-failure';
import { Contract } from '@app/features/contracts/contract.model';
import { embaucheFormValues } from '@app/features/contracts/embauche-dialog/embauche-form-values';
import {
  AssignmentForm,
  AssignmentFormValue,
} from '@app/features/contracts/embauche-dialog/embauche-tabs-form/assignment-form/assignment-form';
import { EmbaucheTabsForm } from '@app/features/contracts/embauche-dialog/embauche-tabs-form/embauche-tabs-form';
import { EmbaucheView } from '@app/features/contracts/embauche-dialog/embauche-view/embauche-view';
import {
  SchedulesForm,
  SchedulesFormValue,
} from '@app/features/contracts/embauche-dialog/embauche-tabs-form/schedules-form/schedules-form';
import { DocumentService } from '@app/features/documents/document-service';
import { CdTDocument } from '@app/features/documents/document.model';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { CopyLocationButton } from '@app/shared/buttons/copy-location-button/copy-location-button';
import { XIconButton } from '@app/shared/buttons/x-icon-button/x-icon-button';
import { fromLocalDateString, toLocalDateString } from '@app/shared/date-utils';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import moment from 'moment';
import { catchError, debounceTime, filter, of, switchMap } from 'rxjs';

/**
 * L'ouverture du dialogue d'un avenant : assez large pour garder le récapitulatif à gauche des
 * onglets, au plus la largeur de l'écran.
 */
export const AMENDMENT_DIALOG_CONFIG = {
  width: '1100px',
  maxWidth: '95vw',
  disableClose: true,
} as const;

export interface AmendmentDialogData {
  contract: Contract;
  /** Absent pour établir un nouvel avenant. */
  amendment?: Amendment;
}

/**
 * Établit, modifie ou suit un avenant (cadrage 2026-014). Les créneaux et les sites partent des
 * conditions en vigueur ; les motifs que le produit en déduit s'affichent pendant la saisie, avec
 * ce qui empêcherait l'enregistrement. Le contrat, lui, reste signé.
 */
@Component({
  selector: 'app-amendment-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogActions,
    MatDialogContent,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonToggleModule,
    AmendmentActionButtons,
    AmendmentChangesView,
    EmbaucheView,
    // Le formulaire du contrat ouvre ses avenants, qui ouvrent ce dialogue : la référence attend.
    forwardRef(() => EmbaucheTabsForm),
    AmendmentSummary,
    ContractStatusComponent,
    SignatureSendFailure,
    SchedulesForm,
    AssignmentForm,
    SaveButton,
    XIconButton,
    CopyLocationButton,
  ],
  templateUrl: './amendment-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./amendment-dialog.scss'],
})
export class AmendmentDialog extends RxjsDirective implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AmendmentDialog>);
  private readonly data = inject<AmendmentDialogData>(MAT_DIALOG_DATA);
  private readonly amendmentService = inject(AmendmentService);
  private readonly documentService = inject(DocumentService);

  @ViewChild(SaveButton) private saveButton?: SaveButton;
  private readonly schedulesForm = viewChild(SchedulesForm);
  private readonly assignmentForm = viewChild(AssignmentForm);

  readonly contract = this.data.contract;
  /** Un avenant anticipé attend la signature de son contrat pour partir en validation. */
  readonly contractSigned = this.contract.status === 'SIGNED';
  readonly amendment = signal<Amendment | undefined>(this.data.amendment);
  /** Les conditions de départ : celles de l'avenant, ou celles en vigueur pour un nouvel avenant. */
  readonly initial = signal<AmendmentPayload | null>(null);

  readonly initialSchedules = computed<SchedulesFormValue | undefined>(() => {
    const initial = this.initial();
    return initial
      ? {
          collectiveSchedules: initial.schedule.collectiveSchedules,
          schedules: initial.schedule.schedules ?? [],
        }
      : undefined;
  });

  readonly initialAssignment = computed<AssignmentFormValue>(() => ({
    assignmentType: this.initial()?.assignmentType ?? 'TOURNEE',
    residencies: this.initial()?.residencies ?? [],
  }));

  readonly declarableReasons = DECLARABLE_REASONS;
  /** Les motifs que l'utilisateur coche, contrôlés par l'API contre les conditions saisies. */
  readonly declaredReasons = new FormGroup({
    WORKING_TIME_INCREASE: new FormControl(false, { nonNullable: true }),
    WORKING_TIME_DECREASE: new FormControl(false, { nonNullable: true }),
    ASSIGNMENT_CHANGE: new FormControl(false, { nonNullable: true }),
    SCHEDULE_CHANGE: new FormControl(false, { nonNullable: true }),
  });
  private readonly declaredReasonsValue = toSignal(this.declaredReasons.valueChanges, {
    initialValue: this.declaredReasons.getRawValue(),
  });

  /** Un avenant à durée indéterminée, ou temporaire jusqu'à une date de fin. */
  readonly duration = new FormControl<'INDETERMINATE' | 'TEMPORARY'>(
    this.data.amendment?.endDate ? 'TEMPORARY' : 'INDETERMINATE',
    { nonNullable: true },
  );
  private readonly durationValue = toSignal(this.duration.valueChanges, {
    initialValue: this.duration.value,
  });
  readonly endDate = new FormControl<unknown>(fromLocalDateString(this.data.amendment?.endDate));
  private readonly endDateValue = toSignal(this.endDate.valueChanges, {
    initialValue: this.endDate.value,
  });

  readonly effectiveDate = new FormControl<unknown>(
    fromLocalDateString(this.data.amendment?.effectiveDate),
    Validators.required,
  );
  private readonly effectiveDateValue = toSignal(this.effectiveDate.valueChanges, {
    initialValue: this.effectiveDate.value,
  });

  readonly readOnly = computed(() => {
    const amendment = this.amendment();
    return this.contract.deleted || (!!amendment && amendment.status !== 'DRAFT');
  });

  /**
   * Un avenant qui ne se saisit plus se consulte en deux modes (RG-avenant-consultation) : ce
   * qu'il change, par défaut, ou le contrat entier tel qu'il le laisse.
   */
  readonly consultation = computed(() => !!this.amendment() && this.readOnly());

  /** L'adresse de l'avenant, qui l'ouvre sur la fiche de son contrat (RG-avenant-consultation). */
  readonly link = computed(() => {
    const amendment = this.amendment();
    return amendment
      ? `${window.location.origin}/contrats/${this.contract.id}/avenants/${amendment.id}`
      : null;
  });
  readonly mode = signal<'changes' | 'complete'>('changes');

  /** Le contrat tel que l'avenant le laisse. */
  readonly amendedContract = computed<Contract | null>(() => {
    const amendment = this.amendment();
    return amendment
      ? {
          ...this.contract,
          schedule: amendment.schedule,
          assignmentType: amendment.assignmentType,
          residencies: amendment.residencies,
        }
      : null;
  });

  readonly amendedValues = computed(() => embaucheFormValues(this.amendedContract() ?? undefined));

  /** Les horaires et l'affectation que l'avenant remplace. */
  readonly previous = computed(() => {
    const previous = this.amendment()?.previousConditions;
    return previous
      ? {
          schedules: {
            collectiveSchedules: previous.schedule.collectiveSchedules,
            schedules: previous.schedule.schedules ?? [],
          } as SchedulesFormValue,
          assignment: {
            assignmentType: previous.assignmentType,
            residencies: previous.residencies,
          } as AssignmentFormValue,
        }
      : null;
  });

  readonly title = computed(() => {
    const number = this.amendment()?.number;
    const start = this.contract.contract.startDate
      ? moment(this.contract.contract.startDate).format('DD/MM/YYYY')
      : '';
    return `Avenant${number ? ' ' + number : ''} au ${this.contract.contract.contractType ?? 'contrat'} du ${start}`;
  });

  readonly employee =
    `${this.contract.employee.lastname ?? ''} ${this.contract.employee.firstname ?? ''}`.trim();
  readonly contractLabel = `${this.contract.contract.contractType ?? ''} · ${this.contract.company?.name ?? ''}`;

  /** Les conditions saisies, quand elles sont assez complètes pour être qualifiées. */
  readonly payload = computed<AmendmentPayload | null>(() => {
    const schedulesForm = this.schedulesForm();
    const assignmentForm = this.assignmentForm();
    const schedules = schedulesForm?.valueSig();
    const assignment = assignmentForm?.valueSig();
    const effectiveDate = toLocalDateString(this.effectiveDateValue() as string | null);
    if (!schedules || !assignment || !effectiveDate) {
      return null;
    }
    const temporary = this.durationValue() === 'TEMPORARY';
    const endDate = temporary ? toLocalDateString(this.endDateValue() as string | null) : null;
    if (temporary && !endDate) {
      return null;
    }
    const declared = this.declaredReasonsValue();
    const reasons = DECLARABLE_REASONS.map((reason) => reason.code).filter(
      (code) => declared[code],
    );
    if (!schedulesForm!.isValid || !assignmentForm!.form.valid) {
      return null;
    }
    const assignmentType = assignment.assignmentType ?? 'TOURNEE';
    return {
      effectiveDate,
      endDate,
      reasons,
      schedule: {
        collectiveSchedules: schedules.collectiveSchedules ?? false,
        schedules: schedules.schedules,
      },
      assignmentType,
      residencies: assignmentType === 'SPECIFIC' ? (assignment.residencies ?? []) : [],
    };
  });

  /** Les motifs déduits par l'API pendant la saisie (RG-avenant-motifs). */
  readonly reasons = toSignal(
    toObservable(this.payload).pipe(
      filter((payload): payload is AmendmentPayload => payload !== null),
      debounceTime(400),
      switchMap((payload) =>
        this.amendmentService
          .reasons(this.contract.id!, payload, this.amendment()?.id)
          .pipe(catchError(() => of(null))),
      ),
    ),
    { initialValue: null as AmendmentReasons | null },
  );

  readonly refusal = computed(() => (this.readOnly() ? null : (this.reasons()?.refusal ?? null)));

  readonly assignmentLabel = computed(() => {
    const payload = this.payload() ?? this.initial();
    if (!payload) {
      return '';
    }
    return payload.assignmentType === 'SPECIFIC'
      ? `Spécifique · ${payload.residencies.length} résidence(s)`
      : 'Tournée';
  });

  readonly durationLabel = computed(() => {
    if (this.durationValue() !== 'TEMPORARY') {
      return 'À durée indéterminée';
    }
    const endDate = toLocalDateString(this.endDateValue() as string | null);
    return endDate ? `Temporaire, jusqu'au ${moment(endDate).format('DD/MM/YYYY')}` : 'Temporaire';
  });

  /** Le nom que portera l'avenant : celui que donnent les motifs saisis, tant qu'il se modifie. */
  readonly fileName = computed(() =>
    this.readOnly()
      ? (this.amendment()?.fileName ?? null)
      : (this.reasons()?.fileName ?? this.amendment()?.fileName ?? null),
  );

  readonly canSave = computed(
    () =>
      !this.readOnly() &&
      this.payload() !== null &&
      !!this.reasons() &&
      this.reasons()!.refusal === null &&
      this.reasons()!.reasons.length > 0,
  );

  ngOnInit(): void {
    const amendment = this.data.amendment;
    if (amendment) {
      this.initial.set({
        effectiveDate: amendment.effectiveDate,
        endDate: amendment.endDate,
        reasons: amendment.reasons.map((reason) => reason.code),
        schedule: amendment.schedule,
        assignmentType: amendment.assignmentType,
        residencies: amendment.residencies,
      });
    } else {
      this.register(
        this.amendmentService
          .workingConditions(this.contract.id!, moment().format('YYYY-MM-DD'))
          .subscribe((conditions) =>
            this.initial.set({
              effectiveDate: '',
              endDate: null,
              reasons: [],
              schedule: conditions.schedule,
              assignmentType: conditions.assignmentType,
              residencies: conditions.residencies,
            }),
          ),
      );
    }
    this.register(
      this.amendmentService.updated$.subscribe((updated) => {
        if (updated.id === this.amendment()?.id) {
          this.amendment.set(updated);
        }
      }),
    );
    amendment?.reasons.forEach((reason) =>
      this.declaredReasons.controls[reason.code].setValue(true),
    );
    this.applyReasonCombinations();
    if (this.readOnly()) {
      this.effectiveDate.disable({ emitEvent: false });
      this.endDate.disable({ emitEvent: false });
      this.duration.disable({ emitEvent: false });
      this.declaredReasons.disable({ emitEvent: false });
    }
  }

  /**
   * Les motifs se combinent : l'augmentation et la diminution s'excluent, et le changement
   * d'horaire ne se coche pas avec l'une d'elles, qui redéfinit déjà les horaires.
   */
  onReasonToggled(code: AmendmentReasonCode, checked: boolean): void {
    const controls = this.declaredReasons.controls;
    if (checked && code === 'WORKING_TIME_INCREASE') {
      controls.WORKING_TIME_DECREASE.setValue(false);
    }
    if (checked && code === 'WORKING_TIME_DECREASE') {
      controls.WORKING_TIME_INCREASE.setValue(false);
    }
    this.applyReasonCombinations();
  }

  private applyReasonCombinations(): void {
    const controls = this.declaredReasons.controls;
    if (controls.WORKING_TIME_INCREASE.value || controls.WORKING_TIME_DECREASE.value) {
      controls.SCHEDULE_CHANGE.setValue(false);
      controls.SCHEDULE_CHANGE.disable();
    } else if (!this.readOnly()) {
      controls.SCHEDULE_CHANGE.enable();
    }
  }

  save(): void {
    const payload = this.payload();
    if (!payload || !this.canSave()) {
      return;
    }
    this.saveButton?.startSaving();
    const amendment = this.amendment();
    const save$ = amendment
      ? this.amendmentService.update(amendment.id, payload)
      : this.amendmentService.establish(this.contract.id!, payload);
    this.register(
      save$.subscribe({
        next: (saved) => {
          this.amendment.set(saved);
          this.saveButton?.stopSaving(true);
        },
        error: () => this.saveButton?.stopSaving(false),
      }),
    );
  }

  openDocument(document: CdTDocument): void {
    this.documentService.preview(document);
  }

  close(): void {
    this.dialogRef.close(this.amendment());
  }
}
